import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getArrayOfAvailableSizes } from '../Context/GarmentContext/garmentContextFunctions';
import { GarmentContext } from '../Context/GarmentContext/GarmentContext';
import { LoggingContext } from '../Context/LoggingContext/LoggingContext';
import { GarmentPricesLogRow } from '../types/databaseTables/garmentpriceslog';
import ColorSelect from './ColorSelect';
import { PageContainer } from './Containers';
import { getImageUrlFromGarmentObject } from './GarmentResult/garmentResultFunctions';
import PercentageFitAndReliability from './PercentageFitAndReliability';
import SizeDropdown from './SizeDropdown';

function ImagesSection() {
  const { garmentObjectState } = useContext(GarmentContext);
  return (
    <Box>
      <Box sx={{ '&>img': { maxHeight: '350px', maxWidth: '270px' } }}>
        <img
          src={getImageUrlFromGarmentObject(garmentObjectState, 'nocolor')}
          alt={`${garmentObjectState.description}`}
        />
      </Box>
      <br />
    </Box>
  );
}
export function getCostsOfThisGarmentFromCostLog(
  costsLogArray: GarmentPricesLogRow[],
  teeTypeId: number
): GarmentPricesLogRow[] {
  return costsLogArray.filter((thisLog) => thisLog.newteetypes_id === teeTypeId);
}

export function MainGarmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    garmentObjectState,
    currentSelectionState,
    setCurrentSelectionState,
    garmentPageGeneralDataState,
  } = useContext(GarmentContext);
  const { addLog } = useContext(LoggingContext);
  const [arrayOfSizesState] = useState(
    getArrayOfAvailableSizes(
      garmentPageGeneralDataState.teeMeasuresAverages,
      garmentObjectState.newteetypes_id
    )
  );
  const firstCost = getCostsOfThisGarmentFromCostLog(
    garmentPageGeneralDataState.costsLogArray,
    garmentObjectState.newteetypes_id
  )[0];

  function getCostText(costLog: GarmentPricesLogRow | undefined) {
    const priceNumber = Number(costLog?.price);
    const currencyCode = costLog?.currencycode;
    if (typeof priceNumber !== 'number' || typeof currencyCode !== 'string')
      return '(no costs have been saved)';
    return `${currencyCode} ${priceNumber}`;
  }

  const updateCurrentSelectionStateColor = useCallback(
    (newValue: string) => {
      setCurrentSelectionState((prev) => ({ ...prev, color: newValue }));
    },
    [currentSelectionState]
  );
  function getMaterialsString(materials: Record<string, number> | null): string {
    if (materials === null) return '(no materials details have been saved)';
    return Object.keys(materials)
      .map(
        (thisMaterialLabel) =>
          `${thisMaterialLabel}: ${materials[thisMaterialLabel as keyof typeof materials] * 100}%`
      )
      .join('; ');
  }

  return (
    <PageContainer>
      <Box // Image, Details
        sx={{
          display: {
            xs: 'block',
            sm: 'grid',
          },
          gridTemplateColumns: '300px 1fr',
        }}
      >
        <ImagesSection />
        {/* Details Section */}
        <Box sx={{ maxWidth: '350px' }}>
          {garmentObjectState.vendorsWebsite ? (
            <a
              onClick={() => addLog('Clicked link to external website.', location.pathname)}
              rel='noopener noreferrer'
              href={`https://${garmentObjectState.vendorsWebsite}`}
              target='_blank'
            >
              <Typography variant='caption' display='block' sx={{ textDecoration: 'none' }}>
                Brand: {garmentObjectState.manufacturer}
              </Typography>
            </a>
          ) : (
            <Typography variant='caption' display='block' sx={{ textDecoration: 'none' }}>
              Brand: {garmentObjectState.manufacturer}
            </Typography>
          )}
          <Typography variant='h5' gutterBottom component='div' sx={{ color: 'text.secondary' }}>
            {garmentObjectState.description}
          </Typography>
          <Typography variant='h6' gutterBottom component='div'>
            {getCostText(firstCost)}
          </Typography>
          <br />
          <SizeDropdown
            values={arrayOfSizesState}
            value={currentSelectionState.size}
            setValue={setCurrentSelectionState}
            label='size'
          />
          <br />
          <br />
          <ColorSelect
            availableColors={garmentObjectState.availablecolors}
            matchingColors={garmentPageGeneralDataState.matchingColors}
            selectedColor={currentSelectionState.color}
            setSelectedColor={updateCurrentSelectionStateColor}
          />
          <Typography variant='caption' display='block' gutterBottom sx={{ fontWeight: 'bold' }}>
            Materials:{' '}
            <Typography
              variant='caption'
              display='block'
              gutterBottom
              sx={{ fontWeight: 'normal' }}
            >
              {getMaterialsString(garmentObjectState.materials)}
            </Typography>
          </Typography>
        </Box>
      </Box>
      <br />
      <Box // Size breakdown button and summary
        sx={{ maxWidth: '350px' }}
      >
        <Typography variant='caption' display='block' gutterBottom sx={{ fontWeight: 'bold' }}>
          Measurements Comparison:
        </Typography>
        <PercentageFitAndReliability
          fitPercentage={garmentObjectState.fit * 100}
          reliabilityPercentage={garmentObjectState.reliability * 100}
          id='fitAndReliabilityGarmentPage'
        />
        <Button
          onClick={() => {
            addLog('Clicked the View Measurements Breakdown Button', location.pathname);
            navigate('../measuresbreakdown');
          }}
          sx={{ margin: 'auto', display: 'block' }}
          variant='outlined'
        >
          View Measurements Breakdown
        </Button>
      </Box>
    </PageContainer>
  );
}
